/**
 * TRITAL Workforce OS™ — Tools Catalog
 *
 * Single source of truth for every tool card on /workforce-os
 * and the matching child route metadata (title, description, status, audience).
 *
 * Status values: "Free Tool" | "AI-Ready" | "MVP" | "Coming Soon"
 */

export const TOOL_CATEGORIES = [
  {
    key: "ai-agents",
    eyebrow: "AI Recruiting Agents",
    headline: "Conversational AI for healthcare recruiting",
    description:
      "Always-on AI co-workers that talk to candidates, support recruiters, and never sleep — engineered for clinical workflows.",
    accent: "navy",
  },
  {
    key: "resume",
    eyebrow: "Resume Intelligence",
    headline: "Format, rank, and present every clinician resume",
    description:
      "Turn raw clinician resumes into recruiter-ready summaries and submission packages in seconds.",
    accent: "medical-blue",
  },
  {
    key: "msp",
    eyebrow: "MSP / VMS Submission Intelligence",
    headline: "Submit clean. Submit faster. Submit confidently.",
    description:
      "Every submission against an MSP/VMS scored against readiness rules before it leaves your inbox.",
    accent: "red",
  },
  {
    key: "pay",
    eyebrow: "Pay, Offer & Placement Intelligence",
    headline: "Build offers that close — without leaving margin on the table",
    description:
      "Pay-package math, offer optimization, and recruiter talking points engineered for healthcare staffing economics.",
    accent: "navy",
  },
  {
    key: "marketplace",
    eyebrow: "Marketplace & Matching",
    headline: "A live operating layer for clinicians and recruiters",
    description:
      "Clinician passports, recruiter marketplace, and AI-matched PRN — connected through the TRITAL workforce graph.",
    accent: "medical-blue",
  },
];

export const TOOLS = [
  // ---------- AI Recruiting Agents ----------
  {
    slug: "ai-nurse-recruiter",
    category: "ai-agents",
    name: "AI Nurse Recruiter — Rebeca Miguel™",
    tagline: "Conversational AI that pre-screens clinicians and routes them to the right next step.",
    audience: "Clinicians + Recruiters",
    cta: "Chat with Rebeca",
    status: "AI-Ready",
    icon: "Bot",
  },
  {
    slug: "recruiter-copilot",
    category: "ai-agents",
    name: "Recruiter Copilot",
    tagline: "AI sidekick that drafts outreach, suggests next-best actions, and handles objections.",
    audience: "Recruiters",
    cta: "Launch Copilot",
    status: "MVP",
    icon: "Sparkles",
  },
  {
    slug: "voice-screening",
    category: "ai-agents",
    name: "AI Voice Screening",
    tagline: "Phone-screen automation for high-volume clinician pipelines.",
    audience: "Recruiters",
    cta: "Join Early Access",
    status: "Coming Soon",
    icon: "Mic",
  },
  {
    slug: "candidate-engagement",
    category: "ai-agents",
    name: "AI Candidate Engagement",
    tagline: "Always-on candidate nurture across SMS, email, and chat.",
    audience: "Recruiters",
    cta: "Join Early Access",
    status: "Coming Soon",
    icon: "MessagesSquare",
  },

  // ---------- Resume Intelligence ----------
  {
    slug: "resume-formatter",
    category: "resume",
    name: "Healthcare Resume Formatter",
    tagline: "Free clinician-friendly resume builder — no signup, instant preview, copy-ready output.",
    audience: "Clinicians",
    cta: "Use Free Tool",
    status: "Free Tool",
    icon: "FileText",
  },
  {
    slug: "ai-screener",
    category: "resume",
    name: "TRITAL AI Screener™",
    tagline: "Paste a candidate resume — get a structured recruiter-ready summary.",
    audience: "Recruiters",
    cta: "Try AI Screener",
    status: "AI-Ready",
    icon: "ClipboardCheck",
  },
  {
    slug: "resume-ranking",
    category: "resume",
    name: "Resume Ranking",
    tagline: "Rank a pile of resumes against any role you paste in.",
    audience: "Recruiters",
    cta: "Join Early Access",
    status: "Coming Soon",
    icon: "ListOrdered",
  },

  // ---------- MSP / VMS ----------
  {
    slug: "submittal-score",
    category: "msp",
    name: "TRITAL Submittal Score™",
    tagline: "Free 8-point submission readiness check before you hit submit on any MSP/VMS.",
    audience: "Recruiters",
    cta: "Score a Submittal",
    status: "Free Tool",
    icon: "BadgeCheck",
  },
  {
    slug: "msp-readiness",
    category: "msp",
    name: "TRITAL MSP Readiness Hub™",
    tagline: "Green / Yellow / Red readiness across compliance, skills, references, pay confirmation.",
    audience: "Recruiters + Compliance",
    cta: "Check Readiness",
    status: "Free Tool",
    icon: "Gauge",
  },
  {
    slug: "recruiter-dashboard",
    category: "msp",
    name: "Unified Recruiter Intelligence Dashboard™",
    tagline: "Every tool, every score, every candidate — one dashboard. (MVP preview)",
    audience: "Recruiters",
    cta: "Preview Dashboard",
    status: "MVP",
    icon: "LayoutDashboard",
  },

  // ---------- Pay / Offer ----------
  {
    slug: "ratelab",
    category: "pay",
    name: "TRITAL RateLab™",
    tagline: "Free pay-package calculator: bill rate, stipend, burden, margin — answers in seconds.",
    audience: "Recruiters + Agencies",
    cta: "Calculate Now",
    status: "Free Tool",
    icon: "Calculator",
  },
  {
    slug: "offer-optimization",
    category: "pay",
    name: "Offer Optimization",
    tagline: "Talking points that close offers when the pay is fixed and the candidate is hesitant.",
    audience: "Recruiters",
    cta: "Generate Talking Points",
    status: "MVP",
    icon: "Target",
  },
  {
    slug: "sms-automation",
    category: "pay",
    name: "SMS Automation",
    tagline: "Compliant, opt-in clinician SMS journeys powered by SimpleTexting.",
    audience: "Recruiters",
    cta: "Join Early Access",
    status: "Coming Soon",
    icon: "MessageCircle",
  },

  // ---------- Marketplace ----------
  {
    slug: "clinician-passport",
    category: "marketplace",
    name: "TRITAL Clinician Passport™",
    tagline: "Free clinician profile readiness summary you can share with recruiters in one click.",
    audience: "Clinicians",
    cta: "Build My Passport",
    status: "Free Tool",
    icon: "IdCard",
  },
  {
    slug: "recruiter-marketplace",
    category: "marketplace",
    name: "TRITAL Recruiter Marketplace™",
    tagline: "A trusted recruiter network for healthcare agencies and MSPs.",
    audience: "Agencies + Recruiters",
    cta: "Join Early Access",
    status: "Coming Soon",
    icon: "Network",
  },
  {
    slug: "shiftbridge-ai",
    category: "marketplace",
    name: "ShiftBridge AI Matching",
    tagline: "AI-matched PRN shifts on the TRITAL ShiftBridge™ marketplace.",
    audience: "Facilities + Clinicians",
    cta: "Learn About ShiftBridge",
    status: "MVP",
    icon: "Zap",
  },
];

export const TOOL_BY_SLUG = Object.fromEntries(TOOLS.map((t) => [t.slug, t]));

export const FREE_TOOL_SLUGS = TOOLS.filter((t) => t.status === "Free Tool" || t.status === "AI-Ready").map(
  (t) => t.slug,
);

// Routes where the floating Rebeca chatbot is allowed.
// Mirrors the activation spec — homepage, candidate-facing public pages,
// Workforce OS tools, and the dynamic skills checklist forms.
export const REBECA_ALLOWED_ROUTES = [
  /^\/$/,                                    // Homepage
  /^\/workforce-os(\/|$)/,                   // Workforce OS hub + tools
  /^\/careers$/,                             // Careers landing
  /^\/careers\/jobs$/,                       // Live jobs page
  /^\/clinicians$/,                          // Clinicians landing
  /^\/register$/,                            // Clinician signup
  /^\/compliance\/checklists(\/|$)/,         // All checklist hubs + per-checklist forms
];

// Routes where Rebeca must NEVER appear.
export const REBECA_BLOCKED_ROUTES = [
  /^\/shiftbridge(\/|$)/,                    // PRN marketplace — fully separate brand
  /^\/dashboard(\/|$)/,                      // Authenticated dashboard
  /^\/login$/,
  /^\/admin(\/|$)/,
];
